import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { ordersApi } from "../services/orders";
import { paymentsApi } from "../services/payments";
import { loadStripe } from "@stripe/stripe-js";
import { PROFILE_ICONS } from "./profileMenuIcons";
import FanClaimButtons from "./FanClaimButtons";
import FanJoinHead from "./FanJoinHead";
import FanJoinSheet from "./FanJoinSheet";
import SetlistArt from "./SetlistArt";
import FanStatusPill from "./FanStatusPill";
import FanProfileHero from "./FanProfileHero";
import FanLiveCard, { LandingOutcome } from "./FanLiveCard";
import {
  FAN_PROFILE_EVENT,
  POINTS_PER_DEVICE_REQUEST,
  clearFanNew,
  listFanOrders,
  upsertFanOrder,
} from "../lib/fanProfile";
import {
  FAN_ACCOUNT_EVENT,
  FanStats,
  claimDeviceOrders,
  ensureFanSession,
  fanFetchMe,
  fanFetchOrders,
  getFanUser,
  getIgDmVerifyCapable,
  isFanSignedIn,
  FanUser,
} from "../lib/fanAccount";
import {
  FanTab,
  inTab,
  FanOrderRow,
  anyLive,
  chipFor,
  counterLive,
  expandedOnLoad,
  groupRows,
  whenLabel,
} from "../lib/fanRequestsModel";
import "./FanProfileSheet.css";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

export type { FanOrderRow } from "../lib/fanRequestsModel";

// 🎟️ My Requests — the fan's home base (founder picks).
//
// One body, two frames:
//   • "page"  — the My Requests tab (FanRequestsPage). Where a fan lands the
//     moment they pay: the request they just made is the LIVE CARD at the
//     top (pick 1A — there is no separate confirmation page any more), the
//     rest of tonight sits beneath it, earlier nights below the line.
//   • "sheet" — the header chip's quick peek on the DJ's pages. Compact rows
//     only; tapping one opens the page with that request expanded, so every
//     money flow has exactly one home.
//
// Two data sources, one render (fan accounts, locked r6):
//   signed OUT — the device registry says WHICH requests are theirs, the
//     server says what state each is in (status-batch, one call);
//   signed IN  — GET /fans/me (account-wide stats) + GET /fans/me/orders
//     (same row shape). If the account fetch fails we fall back to the
//     device rails silently — this surface never dead-ends.
//
// The rules (Tonight / Earlier, pills, chips, which card is open) live in
// lib/fanRequestsModel — pure functions, so nothing here is decided case by
// case. ONE poll for the whole page: every 8s while any row is still open,
// paused while hidden, stopped when nothing can change.

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const isSecure = window.location.protocol === "https:" || window.location.hostname === "localhost";
const stripePromise = STRIPE_PK && isSecure ? loadStripe(STRIPE_PK) : null;

const POLL_MS = 8000;
// Orders this phone already showed the after-paying sign-up sheet for
// (1.5.1 #17) — once per order per device, so a later link to the same
// request (email, push, another tab) never asks again. Newest 50 kept.
const JOIN_SHOWN_KEY = "rq_join_sheet_shown";
const takeJoinSheet = (orderId: string): boolean => {
  try {
    const seen = JSON.parse(localStorage.getItem(JOIN_SHOWN_KEY) || "[]");
    const list: string[] = Array.isArray(seen) ? seen : [];
    if (list.includes(orderId)) return false;
    localStorage.setItem(JOIN_SHOWN_KEY, JSON.stringify([...list, orderId].slice(-50)));
    return true;
  } catch {
    return false;
  }
};
const BOLT = "M13.2 2 5 13.4h5l-1.6 8.6L17 10.4h-5.2Z";

// Whose list is on screen: the signed-in fan, or this device.
const viewerKey = () => (isFanSignedIn() ? `fan:${getFanUser()?._id || ""}` : "device");

/** What the URL said when the fan arrived (FanRequestsPage parses it). */
export type FanLanding = {
  orderId: string | null;
  /** Stripe Checkout return (legacy /success?session_id=cs_…). */
  sessionId: string | null;
  /** Stripe redirect params (3DS / redirect-based methods). */
  redirectStatus: string | null;
  piId: string | null;
  piSecret: string | null;
};

const classifyPi = (s?: string | null): "paid" | "failed" | "unknown" => {
  if (["succeeded", "requires_capture", "processing"].includes(s || "")) return "paid";
  if (["requires_payment_method", "canceled", "requires_action", "failed"].includes(s || "")) return "failed";
  return "unknown";
};

type Props = {
  /** Load + live-update while true (the sheet passes its `open`). */
  active: boolean;
  frame?: "page" | "sheet";
  landing?: FanLanding | null;
  onOpenEditor: () => void;
  /** The little Verify on the name row: My Profile with the message already started. */
  onVerifyInstagram?: () => void;
  /** Someone just signed in at the gate (sheet only: the header decides where they go). */
  onSignedIn?: (fan: FanUser) => void;
  /** Renders the Refer & Earn row when provided, for signed-in fans (sheet only). */
  onOpenRefer?: () => void;
};

const FanRequestsPanel: React.FC<Props> = ({ active, frame = "page", landing = null, onOpenEditor, onSignedIn, onVerifyInstagram, onOpenRefer }) => {
  // A talent who paid on another artist's page (Discover, 1.5.3) pays like a
  // guest and collects no fan points (founder 10/30: keep it simple), so the
  // after-paying "points earned, sign up" sheet never asks them.
  const { user: talent, isAuthenticated } = useContext(AuthContext);
  const talentSignedIn = isAuthenticated && !!talent;
  const [rows, setRows] = useState<FanOrderRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [accountStats, setAccountStats] = useState<FanStats | null>(null);
  // The "All Requests | Playlists" toggle is gone (founder, 1.4.1): playlist
  // sales are parked. When they return, playlists live on the fan's profile
  // mini-dashboard (points card) as a "See my playlists" button — not a tab
  // here. Rows still carry isPlaylist, so the list renders them as before.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tab, setTab] = useState<FanTab>("all");
  const [autoBoostId, setAutoBoostId] = useState<string | null>(null);

  // After-paying sign-up sheet (1.5.1 #17) — which order it is open for. It
  // replaced the gold "points earned" card under the list (removed
  // 2026-10-10): one ask, once per order, never a second one on the page.
  const [joinSheetId, setJoinSheetId] = useState<string | null>(null);

  // Landing (page frame only): what the Stripe redirect / Checkout return
  // told us about the order the fan just paid for.
  const [landingOutcome, setLandingOutcome] = useState<(LandingOutcome & { orderId: string }) | null>(null);
  const [landingDone, setLandingDone] = useState(!landing || (!landing.orderId && !landing.sessionId));
  // Rows we learned about outside the rails (the landing confirm) — merged
  // in whenever the rails don't carry them yet.
  const extraRowsRef = useRef<Map<string, FanOrderRow>>(new Map());
  const initDoneRef = useRef(false);
  const counterSeenRef = useRef<Set<string>>(new Set());
  // Which viewer (viewerKey) the rows on screen belong to; null = none yet.
  // A change for the SAME viewer (a name edit, a boost, a device write)
  // refreshes in place — blanking to "Loading…" unmounted the live card with
  // its Boost sheet, card form or counter payment (2026-10-10).
  const rowsForRef = useRef<string | null>(null);

  // Re-read identity (device + account) whenever either store changes —
  // the editor stacked on top writes both while this surface is visible.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const bump = () => setTick((t) => t + 1);
    window.addEventListener(FAN_PROFILE_EVENT, bump);
    window.addEventListener(FAN_ACCOUNT_EVENT, bump);
    return () => {
      window.removeEventListener(FAN_PROFILE_EVENT, bump);
      window.removeEventListener(FAN_ACCOUNT_EVENT, bump);
    };
  }, [active]);

  // Seeing this surface IS "seeing where it went" — the header dot clears.
  useEffect(() => {
    if (active) clearFanNew();
  }, [active]);

  const mergeRow = useCallback((patch: Partial<FanOrderRow> & { orderId: string }) => {
    extraRowsRef.current.set(patch.orderId, {
      ...(extraRowsRef.current.get(patch.orderId) || ({} as FanOrderRow)),
      ...patch,
    } as FanOrderRow);
    setRows((prev) => {
      if (!prev) return prev;
      const i = prev.findIndex((r) => r.orderId === patch.orderId);
      if (i === -1) return [{ ...(patch as FanOrderRow) }, ...prev];
      const next = prev.slice();
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }, []);

  /* ── landing: the payment that just happened (ported from /success) ── */
  useEffect(() => {
    if (!active || frame !== "page" || !landing || landingDone) return;
    let cancelled = false;
    (async () => {
      try {
        if (landing.sessionId) {
          const res = await paymentsApi.confirmCheckoutSession(landing.sessionId);
          if (cancelled) return;
          const o = res;
          if (o?.orderId) {
            upsertFanOrder(o.orderId);
            if (o.djSlug) sessionStorage.setItem("lastDjSlug", o.djSlug);
            mergeRow(o);
            setLandingOutcome({ orderId: o.orderId });
          }
          return;
        }
        const orderId = landing.orderId as string;
        upsertFanOrder(orderId);

        // Item 9 — Stripe redirect flows land here with redirect params.
        // Verify the PI actually succeeded before confirming/celebrating; a
        // declined 3DS redirect otherwise looks identical to a success.
        let outcome: "paid" | "failed" | "unknown" = "unknown";
        if (landing.piSecret) {
          const stripe = stripePromise ? await stripePromise : null;
          if (stripe) {
            try {
              const { paymentIntent } = await stripe.retrievePaymentIntent(landing.piSecret);
              outcome = classifyPi(paymentIntent?.status);
            } catch {
              /* fall back to redirect_status below */
            }
          }
        }
        if (outcome === "unknown" && landing.redirectStatus) outcome = classifyPi(landing.redirectStatus);
        if (cancelled) return;

        if (outcome === "failed") {
          try {
            const res2 = await ordersApi.status(orderId);
            if (cancelled) return;
            const o = res2;
            if (o?.djSlug) sessionStorage.setItem("lastDjSlug", o.djSlug);
            mergeRow(o);
            // "The order's own payment failed" vs "a boost / counter-accept
            // failed on an order that's still live" — the second must NOT
            // show the full-failure card (the original hold is still on the
            // card, the request still in the artist's queue).
            if (o.paid === true || counterLive(o)) setLandingOutcome({ orderId, softFail: true });
            else setLandingOutcome({ orderId, paymentFailed: true });
          } catch {
            if (cancelled) return;
            mergeRow({ orderId, status: "frozen", paid: false, searchQuery: "" });
            setLandingOutcome({ orderId, paymentFailed: true });
          }
          return;
        }

        try {
          // Pass the redirect's PI id explicitly — after a redirected BOOST
          // the order still points at the stale previous PI, and confirm-intent
          // must apply the boost from the NEW one.
          const res = await paymentsApi.confirmIntent(orderId, landing.piId || undefined);
          if (cancelled) return;
          if (res?.djSlug) sessionStorage.setItem("lastDjSlug", res.djSlug);
          mergeRow({ ...res, orderId });
        } catch {
          try {
            const res2 = await ordersApi.status(orderId);
            if (cancelled) return;
            if (res2?.djSlug) sessionStorage.setItem("lastDjSlug", res2.djSlug);
            mergeRow({ ...res2, orderId });
          } catch {
            /* rails will carry it, or the row shows once the poll lands */
          }
        }
        if (!cancelled) setLandingOutcome({ orderId });
      } catch (err) {
        // A Checkout confirm that fails (session already consumed, network
        // blip) — the rails still show whatever is true; never a dead end.
        console.error("landing confirm error:", err);
      } finally {
        if (!cancelled) setLandingDone(true);
        // Safety net (2026-10-10): a signed-in fan's order that went out
        // without their token sits outside the account — shown once, then
        // gone, no points. Claim this device's orders into the account
        // (idempotent, orders only), then refresh in place if any moved.
        // Never blocks the landing; a failure changes nothing.
        void (async () => {
          await ensureFanSession();
          if (!isFanSignedIn()) return;
          try {
            if ((await claimDeviceOrders()) > 0) setTick((t) => t + 1);
          } catch {}
        })();
      }
    })();
    return () => {
      cancelled = true;
    };
    // This confirms a payment. It runs once per landing; mergeRow is a
    // per-render helper and must not re-trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, frame, landing, landingDone]);

  // The request went through and the fan has no account: ask them to sign up
  // NOW — once per order per phone (a refresh, a second visit or a later
  // email/push link to the same request never re-asks).
  useEffect(() => {
    const id = landingOutcome?.orderId;
    if (!id || landingOutcome?.paymentFailed || landingOutcome?.softFail || frame !== "page" || isFanSignedIn()) return;
    if (takeJoinSheet(id)) setJoinSheetId(id);
  }, [landingOutcome, frame]);

  /* ── rows: one loader for first paint, refreshes and every poll ──────── */
  // "first" starts over ("Loading…"); "refresh" updates in place, stats
  // included; "poll" updates in place, orders only.
  const load = useCallback(
    async (mode: "first" | "refresh" | "poll") => {
      if (mode === "first") {
        rowsForRef.current = null;
        setRows(null);
        setFailed(false);
      }
      await ensureFanSession();
      const viewer = viewerKey();
      const withExtras = (raw: FanOrderRow[]) => {
        // Only requests the fan actually made: an order is created the
        // moment a song is picked (the payment intent needs it), and the
        // account rail returned those too — "songs I merely selected" showed
        // up as requests (founder, 1.4.1). Unpaid never reaches the DJ, so
        // it never belongs here. `paid` missing = a partial landing row we
        // built ourselves; keep it.
        const list = raw.filter((r) => r.paid !== false);
        const ids = new Set(list.map((r) => r.orderId));
        const extras = Array.from(extraRowsRef.current.values()).filter((r) => !ids.has(r.orderId));
        // Extras we DO have on the rails: the rails win (fresh server truth).
        return extras.length ? [...extras, ...list] : list;
      };
      const show = (raw: FanOrderRow[]) => {
        rowsForRef.current = viewer;
        setRows(withExtras(raw));
      };

      if (isFanSignedIn()) {
        try {
          // ONE call per poll: the stats line only refreshes on first paint
          // and on a refresh (an account change, a boost) — polls fetch orders.
          const [me, orders] = await Promise.all([
            mode === "poll" ? Promise.resolve(null) : fanFetchMe(),
            fanFetchOrders<FanOrderRow>(),
          ]);
          if (me) setAccountStats(me.stats);
          show(orders);
          return;
        } catch {
          // Account fetch hiccup — the device rails still tell the story.
        }
      }
      if (mode === "first") setAccountStats(null);

      const refs = listFanOrders();
      if (!refs.length) {
        show([]);
        return;
      }
      const ids = refs.map((r) => r.id);
      const orderRank = new Map(ids.map((id, i) => [id, i]));
      const sortByRegistry = (list: FanOrderRow[]) =>
        [...list].sort((a, b) => (orderRank.get(a.orderId) ?? 999) - (orderRank.get(b.orderId) ?? 999));
      try {
        const res = await ordersApi.statusBatch(ids);
        show(sortByRegistry(Array.isArray(res?.orders) ? res.orders : []));
      } catch {
        // Old server without the batch endpoint — fall back to the per-id
        // endpoint for the freshest few. Capped low on purpose: the global
        // rate limit is shared by everyone on the venue's WiFi.
        try {
          const subset = ids.slice(0, 10);
          const results = await Promise.all(
            subset.map((id) =>
              ordersApi
                .status(id)
                .then((r) => ({ ...r }))
                .catch(() => null)
            )
          );
          show(sortByRegistry(results.filter(Boolean) as FanOrderRow[]));
        } catch {
          if (mode === "first") setFailed(true);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (!active || !landingDone) {
      rowsForRef.current = null; // closed: the next open starts over, as before
      return;
    }
    void load(rowsForRef.current === viewerKey() ? "refresh" : "first");
  }, [active, tick, landingDone, load]);

  // ONE poll for the page: every 8s while any row is still open, paused
  // while hidden (instant catch-up on return / bfcache restore), stopped
  // when nothing can change. A failed landing payment shows the retry card
  // — no polling behind it so a later artist action can't fight it.
  const live = !!rows && anyLive(rows) && !landingOutcome?.paymentFailed;
  useEffect(() => {
    if (!active || !live) return;
    const tickPoll = () => {
      if (document.hidden) return;
      void load("poll");
    };
    const id = setInterval(tickPoll, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) void load("poll");
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void load("poll");
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [active, live, load]);

  /* ── grouping + which card is open ───────────────────────────────────── */
  const groups = useMemo(() => groupRows(rows || []), [rows]);

  useEffect(() => {
    if (!rows || frame !== "page") return;
    if (!initDoneRef.current) {
      initDoneRef.current = true;
      // Counters already on the page at first paint are "seen" — only one
      // that LANDS later may reopen itself over whatever the fan is doing.
      groups.tonight.filter(counterLive).forEach((r) => counterSeenRef.current.add(r.orderId));
      setExpandedId(expandedOnLoad(groups.tonight, landingOutcome?.orderId || landing?.orderId || null));
      return;
    }
    // Money questions jump the line: a counter that just landed opens itself.
    const c = groups.tonight.find((r) => counterLive(r) && !counterSeenRef.current.has(r.orderId));
    if (c) {
      counterSeenRef.current.add(c.orderId);
      setTab("all");
      setExpandedId(c.orderId);
    }
  }, [rows, groups.tonight, frame, landingOutcome, landing]);

  const stats = useMemo(() => {
    const list = rows || [];
    if (accountStats) return { requests: accountStats.requests, played: accountStats.played };
    return { requests: list.length, played: list.filter((r) => r.status === "played").length };
  }, [rows, accountStats]);

  if (!active) return null;

  const account = getFanUser();
  const signedIn = isFanSignedIn();
  const name = (account && account.name) || "";
  const instagram = (account && account.instagram) || "";
  const igSub = instagram && name !== `@${instagram}` ? `@${instagram}` : "";
  // The tick and the little Verify beside the name (founder pick C, 2026-11-02):
  // Verify only while the server has "Verify with a DM" on for this fan.
  const igVerified = !!account?.instagramVerified && !!instagram;
  const showVerify = signedIn && !!name && !igVerified && !!onVerifyInstagram && getIgDmVerifyCapable();
  const points = accountStats?.points || 0;
  const boostCost = accountStats?.pointsBoostCost || 300;
  const deviceCount = listFanOrders().length;
  const landingId = landingOutcome?.orderId || landing?.orderId || null;

  const onPointsBoosted = (orderId: string, newBalance: number) => {
    // A real boost only (the server said yes): flip the row gold and take the
    // fresh balance; the next poll re-confirms from the server. A refused one
    // (409) re-reads the list instead — onRefresh (2026-10-10).
    mergeRow({ orderId, fanBoost: true });
    setAccountStats((st) => (st ? { ...st, points: newBalance } : st));
  };

  const openPage = (r: FanOrderRow) => {
    window.location.href = `/fan/requests?order=${encodeURIComponent(r.orderId)}`;
  };
  const openPlaylist = (r: FanOrderRow) => {
    if (r.receiptId) window.location.href = `/playlist/${encodeURIComponent(r.receiptId)}`;
  };
  const isDeliveredPlaylist = (r: FanOrderRow) => !!(r.isPlaylist && r.receiptId && r.playlistDeliveredAt);

  const onRowTap = (r: FanOrderRow) => {
    if (isDeliveredPlaylist(r)) return openPlaylist(r);
    if (frame === "sheet") return openPage(r);
    setExpandedId((cur) => (cur === r.orderId ? null : r.orderId));
  };

  const onChip = (r: FanOrderRow, kind: "boost" | "respond" | "retry") => {
    if (kind === "retry") {
      window.location.href = r.djSlug ? `/dj/${r.djSlug}/request?prev_order_id=${r.orderId}` : "/fan";
      return;
    }
    if (frame === "sheet") return openPage(r);
    setExpandedId(r.orderId);
    if (kind === "boost") setAutoBoostId(r.orderId);
  };

  const chipLabel = (kind: "boost" | "respond" | "retry") =>
    kind === "boost" ? (
      <>
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={BOLT} /></svg>
        {tr("fanRequestsPanel.boost")}
      </>
    ) : kind === "respond" ? (
      tr("fanRequestsPanel.respond")
    ) : (
      tr("fanRequestsPanel.tryAgain")
    );

  const compactRow = (r: FanOrderRow, inTonight: boolean) => {
    const chip = chipFor(r, { signedIn, points, boostCost }, inTonight);
    const delivered = isDeliveredPlaylist(r);
    return (
      <div
        key={r.orderId}
        className={`fps-row${counterLive(r) ? " fps-row--counter" : ""}${
          r.fanBoost && (r.status === "pending" || r.status === "accepted" || r.status === "queued") ? " fps-row--fanboost" : ""
        }${inTonight && landingId === r.orderId && expandedId !== r.orderId ? " fps-row--new" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => onRowTap(r)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onRowTap(r);
        }}
      >
        {r.trackImages && r.trackImages.length ? (
          <SetlistArt images={r.trackImages} className="fps-art" />
        ) : r.trackImage ? (
          <img className="fps-art" src={r.trackImage} alt="" />
        ) : (
          <div className="fps-art fps-art--ph" aria-hidden="true">♪</div>
        )}
        <div className="fps-row-meta">
          <div className="fps-row-song">{r.searchQuery || (r.isPlaylist ? tr("fanRequestsPanel.playlist") : "—")}</div>
          <div className="fps-row-sub">
            {(r.djName || r.djSlug || "").trim()}
            {delivered
              ? r.playlistDeliveredAt ? tr("fanRequestsPanel.deliveredWhen", { when: whenLabel(r.playlistDeliveredAt) }) : tr("fanRequestsPanel.deliveredDot")
              : r.createdAt
              ? ` · ${whenLabel(r.createdAt)}`
              : ""}
          </div>
        </div>
        <span className="fps-right">
          {delivered ? <span className="fps-pill fps-pill--open">{tr("fanRequestsPanel.open")}</span> : <FanStatusPill row={r} />}
          {chip && (
            <button
              type="button"
              className={`fps-chip fps-chip--${chip}`}
              onClick={(e) => {
                e.stopPropagation();
                onChip(r, chip);
              }}
            >
              {chipLabel(chip)}
            </button>
          )}
        </span>
      </div>
    );
  };

  // "Wasn't played?" (founder, 2026-10-09): a fan may claim up to 7 days after
  // a request was played, so an Earlier row that still has the claim door (or
  // a claim on it) opens its live card on tap, like tonight's rows. Every
  // other Earlier row stays a compact row with its final pill.
  const claimCard = (r: FanOrderRow) => r.status === "played" && (r.claimable === true || !!r.claim);

  const renderRow = (r: FanOrderRow, inTonight: boolean) => {
    if (frame === "page" && (inTonight || claimCard(r)) && expandedId === r.orderId && !isDeliveredPlaylist(r)) {
      return (
        <FanLiveCard
          key={r.orderId}
          row={r}
          landing={inTonight && landingId === r.orderId ? landingOutcome : null}
          isNew={false}
          signedIn={signedIn}
          points={points}
          boostCost={boostCost}
          autoOpenBoost={autoBoostId === r.orderId}
          onUpdate={mergeRow}
          onPointsBoosted={onPointsBoosted}
          onRefresh={() => setTick((t) => t + 1)}
          onCollapse={() => {
            setExpandedId(null);
            setAutoBoostId(null);
          }}
        />
      );
    }
    return compactRow(r, inTonight);
  };

  const tonightShown = groups.tonight.filter((r) => inTab(r, tab));
  const earlierShown = groups.earlier.filter((r) => inTab(r, tab));
  const list = (
    <>
      {tonightShown.length > 0 && (
        <>
          <div className="fps-div fps-div--live">{groups.tonightLabel === "Tonight" ? tr("fanRequestsPanel.tonight") : groups.tonightLabel === "Last night" ? tr("fanRequestsPanel.lastNight") : null}</div>
          <div className="fps-list">{tonightShown.map((r) => renderRow(r, true))}</div>
        </>
      )}
      {earlierShown.length > 0 && (
        <>
          {tonightShown.length > 0 && <div className="fps-div">{tr("fanRequestsPanel.earlier")}</div>}
          <div className={`fps-list fps-list--past${tonightShown.length > 0 ? "" : " fps-list--first"}`}>{earlierShown.map((r) => renderRow(r, false))}</div>
        </>
      )}
      {groups.tonight.length > 0 &&
        groups.earlier.length === 0 &&
        frame === "page" &&
        !groups.tonight.some((r) => r.isPlaylist) && (
          <div className="fps-trust">{tr("fanRequestsPanel.youReOnlyCharged")}</div>
        )}
    </>
  );

  return (
    <>
      {name && <h1 className="fps-title">{tr("fanRequestsPanel.myProfile")}</h1>}
      <FanProfileHero
        name={name}
        handle={igSub}
        verified={igVerified}
        showVerify={showVerify}
        avatarUrl={account?.avatarUrl}
        showBadge={!signedIn}
        level={signedIn ? accountStats?.level : 0}
        counts={signedIn && accountStats ? { requests: stats.requests, played: stats.played, points, boostCost } : null}
        onOpen={onOpenEditor}
        onVerify={onVerifyInstagram}
      />

      {/* Fan Refer & Earn (founder 10/29): the one way in — Profile sheet
          only, signed-in fans only, so every point has an owner. */}
      {onOpenRefer && signedIn && (
        <div className="fph-tiles">
          <button type="button" className="fps-lb-row fps-refer-row" onClick={onOpenRefer}>
            <span className="fps-lb-ic fps-refer-ic" aria-hidden="true">{PROFILE_ICONS.refer}</span>
            <span className="fps-refer-txt">
              <span className="fps-lb-txt">{tr("referEarnSheet.referEarn2")}</span>
              <span className="fps-refer-sub">{tr("fanReferEarn.rowSub")}</span>
            </span>
            <span className="fps-lb-chev" aria-hidden="true">›</span>
          </button>
        </div>
      )}

      {!signedIn && deviceCount === 0 && !landingId ? (
        // The gate (founder decision): no profile AND nothing on this phone
        // → one bold line, three doors. The moment this phone makes a
        // request, the list appears (the after-paying sheet asks once).
        <div className="fps-gate">
          <FanJoinHead size="sm" />
          <FanClaimButtons compact onSignedIn={onSignedIn} />
        </div>
      ) : (
        <>
          <h2 className="fps-heading">{tr("fanRequestsPanel.myRequests")}</h2>

          {failed ? (
            <div className="fps-empty">
              <T k="fanRequestsPanel.couldnTLoadYour" tags={{ 0: (c) => <button type="button" className="fps-link" onClick={() => setTick((t) => t + 1)}>{c}</button> }} />
            </div>
          ) : rows === null ? (
            <div className="fps-empty">{tr("fanRequestsPanel.loading")}</div>
          ) : rows.length === 0 ? (
            <div className="fps-none">
              <div className="fps-none-t">{tr("fanRequestsPanel.noRequestsYet")}</div>
              <div className="fps-none-s">{tr("fanRequestsPanel.findTheDjYou")}</div>
              <a className="fps-none-b" href="/fan">{tr("fanRequestsPanel.findADj")}</a>
            </div>
          ) : (
            <>
              <div className="fph-tabs" role="tablist">
                {(
                  [
                    ["all", tr("fanRequestsPanel.tabAll")],
                    ["played", tr("fanStatusPill.played")],
                    ["declined", tr("fanStatusPill.declined")],
                    ["playlists", tr("fanRequestsPanel.tabPlaylists")],
                  ] as [FanTab, string][]
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={tab === key}
                    className={`fph-tab${tab === key ? " fph-tab--on" : ""}`}
                    onClick={() => setTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {list}
              {tonightShown.length === 0 && earlierShown.length === 0 && <div className="fph-empty">{tr("fanRequestsPanel.tabEmpty")}</div>}
            </>
          )}

          {account?.email && <div className="fps-foot">{tr("fanRequestsPanel.signedInAs", { email: account.email })}</div>}
        </>
      )}

      <FanJoinSheet
        open={!!joinSheetId && !signedIn && !talentSignedIn}
        points={deviceCount * POINTS_PER_DEVICE_REQUEST}
        onClose={() => setJoinSheetId(null)}
      />
    </>
  );
};

export default FanRequestsPanel;
